import React, { useState, useEffect } from 'react';
import { Eye, Download, CreditCard, Search, Filter, Calendar, TrendingUp, Printer, Send } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { formatCurrency, formatDate, downloadPDF, sendEmail } from '../../lib/utils';
import { paymentService } from '../../lib/database/payments';
import { useAuth } from '../../hooks/useAuth';
import { ViewPaymentModal } from './ViewPaymentModal';
import { PrintablePayment } from '../print/PrintablePayment';

export const ClientPayments: React.FC = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [paymentToPrint, setPaymentToPrint] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState<{[key: string]: boolean}>({});

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const { data } = await paymentService.getPayments();
      if (data) {
        // Filter payments for current client
        const clientPayments = data.filter(payment => 
          payment.invoice?.client?.email === user?.email
        );
        setPayments(clientPayments);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchPayments();
    }
  }, [user?.email]);

  const handleViewPayment = (payment: any) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  };

  const handlePrintPayment = (payment: any) => {
    setPaymentToPrint(payment);
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadReceipt = async (payment: any) => {
    setActionLoading({...actionLoading, [`download_${payment.id}`]: true});
    try {
      // Generate and download receipt PDF
      const filename = `Payment_Receipt_${payment.id.slice(-8)}`;
      const content = JSON.stringify({
        paymentId: payment.id,
        invoiceNumber: payment.invoice?.invoice_number,
        client: payment.invoice?.client?.name,
        amount: payment.amount,
        method: payment.method,
        date: payment.payment_date
      });
      
      await downloadPDF(filename, content);
      
      // Show success message
      alert("Payment receipt downloaded successfully");
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    } finally {
      setActionLoading({...actionLoading, [`download_${payment.id}`]: false});
    }
  };

  const handleEmailReceipt = async (payment: any) => {
    setActionLoading({...actionLoading, [`email_${payment.id}`]: true});
    try {
      // Send email with payment receipt
      const userEmail = user?.email || payment.invoice?.client?.email || '';
      const subject = `Payment Receipt for ${payment.invoice?.invoice_number || 'Invoice'}`;
      const body = `Thank you for your payment of ${formatCurrency(Number(payment.amount))} made on ${formatDate(payment.payment_date)}. Please find attached your receipt.`;
      
      await sendEmail(userEmail, subject, body);
      
      // Show success message
      alert(`Payment receipt has been sent to ${userEmail}`);
    } catch (error) {
      console.error('Error sending receipt:', error);
      alert('Failed to send receipt. Please try again.');
    } finally {
      setActionLoading({...actionLoading, [`email_${payment.id}`]: false});
    }
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'card':
        return 'bg-primary-100 text-primary-700 border-primary-200';
      case 'bank_transfer':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'upi':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'cheque':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.invoice?.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.reference_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || payment.method === filter;
    
    return matchesSearch && matchesFilter;
  });

  const totalPaid = payments.reduce((sum, payment) => sum + Number(payment.amount), 0);
  const thisYearPayments = payments.filter(payment => {
    const paymentYear = new Date(payment.payment_date).getFullYear();
    const currentYear = new Date().getFullYear();
    return paymentYear === currentYear;
  });
  const thisYearTotal = thisYearPayments.reduce((sum, payment) => sum + Number(payment.amount), 0);

  const paymentMethods = ['cash', 'card', 'bank_transfer', 'upi', 'cheque'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Payment History</h1>
            <p className="text-gray-600 mt-2">Track all your payment transactions</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Paid</p>
            <p className="text-2xl font-bold text-success-600">{formatCurrency(totalPaid)}</p>
          </div>
        </div>

        {/* Payment Summary */}
        <Card className="bg-gradient-to-r from-success-50 to-success-100 border-success-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-success-800 font-semibold mb-2">Payment Summary</h3>
              <p className="text-success-700">
                You've made {payments.length} payment{payments.length !== 1 ? 's' : ''} totaling {formatCurrency(totalPaid)}
              </p>
              <p className="text-success-600 text-sm mt-1">
                This year: {formatCurrency(thisYearTotal)} across {thisYearPayments.length} transaction{thisYearPayments.length !== 1 ? 's' : ''}
              </p>
            </div>
            <TrendingUp className="w-12 h-12 text-success-600" />
          </div>
        </Card>

        {/* Search and Filters */}
        <Card padding="sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search payments or invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Method:</span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    filter === 'all'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  All
                </button>
                {paymentMethods.map((method) => (
                  <button
                    key={method}
                    onClick={() => setFilter(method)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 capitalize ${
                      filter === method
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {method.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Payments List */}
        <div className="space-y-4">
          {filteredPayments.length === 0 ? (
            <Card className="text-center py-12">
              <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filter !== 'all' ? 'No payments found' : 'No payments yet'}
              </h3>
              <p className="text-gray-600">
                {searchTerm || filter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Your payment history will appear here'
                }
              </p>
            </Card>
          ) : (
            filteredPayments.map((payment) => (
              <Card key={payment.id} hover className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-success-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Payment #{payment.id.slice(-8)}
                      </h3>
                      <p className="text-gray-600">
                        Invoice: {payment.invoice?.invoice_number || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Paid on {formatDate(payment.payment_date)}
                      </p>
                      {payment.reference_id && (
                        <p className="text-sm text-gray-500">
                          Ref: {payment.reference_id}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-success-600">
                        {formatCurrency(Number(payment.amount))}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${getMethodColor(payment.method)}`}>
                        {payment.method.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Eye}
                        onClick={() => handleViewPayment(payment)}
                      >
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Printer}
                        onClick={() => handlePrintPayment(payment)}
                      >
                        Print
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Download}
                        onClick={() => handleDownloadReceipt(payment)}
                        loading={actionLoading[`download_${payment.id}`]}
                        disabled={actionLoading[`download_${payment.id}`]}
                      >
                        Download Receipt
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Send}
                        onClick={() => handleEmailReceipt(payment)}
                        loading={actionLoading[`email_${payment.id}`]}
                        disabled={actionLoading[`email_${payment.id}`]}
                      >
                        Email Receipt
                      </Button>
                    </div>
                  </div>
                </div>
                
                {payment.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600">{payment.notes}</p>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card padding="sm" className="text-center bg-gradient-to-br from-success-50 to-success-100 border-success-200">
            <p className="text-2xl font-bold text-success-700">{formatCurrency(totalPaid)}</p>
            <p className="text-sm text-success-600">Total Paid</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <p className="text-2xl font-bold text-blue-700">{payments.length}</p>
            <p className="text-sm text-blue-600">Total Payments</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
            <p className="text-2xl font-bold text-primary-700">
              {payments.filter(p => p.method === 'card').length}
            </p>
            <p className="text-sm text-primary-600">Card Payments</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <p className="text-2xl font-bold text-purple-700">
              {payments.filter(p => p.method === 'upi').length}
            </p>
            <p className="text-sm text-purple-600">UPI Payments</p>
          </Card>
        </div>

        {/* View Payment Modal */}
        <ViewPaymentModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedPayment(null);
          }}
          payment={selectedPayment}
        />
      </div>

      {/* Printable Version */}
      <PrintablePayment payment={paymentToPrint} isVisible={showPrintView} />
    </>
  );
};